import { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import CustomModal from '../CustomModal';
import Button from '../../Button';
import Text from '../../Text';
import SkeletonBone, { SkeletonStack } from '../../SkeletonBone';
import { styles } from '../../../styles/themes/style';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import { useAppActions } from '../../../stores/application/useAppActions';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import {
  ISuppressionAddResult, ISuppressionList, ISuppressionPreview, SuppressionType,
} from '../../../interfaces/models/campaign';
import { describeListScope } from '../../../utils/suppressionListUtils';

/**
 * "Add to suppression list" — from Settings, and from a campaign lead's sidebar (which
 * opens it with the lead's profile, domain or address already filled in).
 *
 * Two steps. The values are checked first (nothing saved); when adding them would take
 * leads out of running campaigns, or some values could not be read, the modal shows
 * that and asks before saving. Otherwise it saves straight away.
 */

const ADD_TYPES: { key: SuppressionType; label: string; placeholder: string; hint: string }[] = [
  {
    key: 'profile',
    label: 'LinkedIn profiles',
    placeholder: 'https://www.linkedin.com/in/jane-doe\nhttps://www.linkedin.com/sales/lead/ACwAA…\njohn-smith-123',
    hint: 'A profile URL, a Sales Navigator lead URL, or the vanity name.',
  },
  {
    key: 'domain',
    label: 'Company domains',
    placeholder: 'acme.com\nhttps://www.globex.io',
    hint: 'Everyone at the company, matched on the lead’s company website or email. Subdomains are included.',
  },
  {
    key: 'email',
    label: 'Email addresses',
    placeholder: 'jane@acme.com\njohn@globex.io',
    hint: 'The person is skipped on every channel, not only email.',
  },
];

type InvalidValue = { value: string; reason: string };

function splitValues(text: string): string[] {
  return text.split(/[\n,;]+/).map(v => v.trim()).filter(Boolean);
}

function plural(n: number, word: string, many = `${word}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? word : many}`;
}

interface IAddToSuppressionListModalProps {
  /** The list to add to first; the workspace list when absent. */
  listId?: string;
  initialType?: SuppressionType;
  initialValue?: string;
  /** 'lead' when opened from a campaign lead — shown on the entry in Settings. */
  source?: 'manual' | 'lead';
  /** After entries were saved, so whatever is behind the modal can reload. */
  onAdded?: (listId: string) => void;
}

const AddToSuppressionListModal: FunctionComponent<IAddToSuppressionListModalProps> = ({
  listId: initialListId, initialType = 'profile', initialValue = '', source = 'manual', onAdded,
}) => {
  const { hideModal } = useAppHandlers();
  const { setToast } = useAppActions();
  const { getSuppressionLists, previewSuppressions, addSuppressions } = useCampaignsRequest();
  const getListsRef = useRef(getSuppressionLists);
  getListsRef.current = getSuppressionLists;

  const [type, setType] = useState<SuppressionType>(initialType);
  const [text, setText] = useState(initialValue);
  const [note, setNote] = useState('');
  const [invalid, setInvalid] = useState<InvalidValue[]>([]);
  /** Set once the values were checked and need a second look: the confirm step. */
  const [preview, setPreview] = useState<ISuppressionPreview | null>(null);
  const [busy, setBusy] = useState(false);

  // Only the lists this viewer may add to are offered.
  const [lists, setLists] = useState<ISuppressionList[]>([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [userId, setUserId] = useState<string>();
  const [listId, setListId] = useState(initialListId ?? '');

  useEffect(() => {
    let cancelled = false;
    void getListsRef.current().then(res => {
      if (cancelled) return;
      setListsLoaded(true);
      if (!res?.success) return;
      const addable: ISuppressionList[] = res.data.lists.filter((l: ISuppressionList) => l.canAdd);
      setLists(addable);
      setUserId(res.data.userId);
      setListId(current => (current && addable.some(l => l._id === current)
        ? current
        : addable.find(l => l.isDefault)?._id ?? addable[0]?._id ?? ''));
    });
    return () => { cancelled = true; };
  }, []);

  const values = splitValues(text);
  const meta = ADD_TYPES.find(t => t.key === type)!;
  const selectedList = lists.find(l => l._id === listId);

  const commit = async () => {
    setBusy(true);
    const res = await addSuppressions({
      ...(listId && { listId }), type, values, source, ...(note.trim() && { note: note.trim() }),
    });
    setBusy(false);

    if (!res?.success) {
      setPreview(null);
      setInvalid(res?.data?.invalid ?? []);
      setToast({ type: 'error', message: res?.message || 'Could not add to the suppression list.' });
      return;
    }

    const r: ISuppressionAddResult = res.data;
    const parts = [`${plural(r.added, 'entry', 'entries')} added`];
    if (r.alreadyListed) parts.push(`${r.alreadyListed.toLocaleString()} already listed`);
    if (r.invalid.length) parts.push(`${r.invalid.length.toLocaleString()} skipped`);
    if (r.leadsRemoved) parts.push(`${plural(r.leadsRemoved, 'lead')} removed from ${plural(r.campaignsAffected, 'campaign')}`);
    setToast({ type: 'success', message: `${parts.join(' · ')}${selectedList ? ` — ${selectedList.name}` : ''}` });

    hideModal();
    onAdded?.(listId);
  };

  const check = async () => {
    if (!values.length || busy) return;

    setBusy(true);
    const res = await previewSuppressions({ ...(listId && { listId }), type, values });
    setBusy(false);
    if (!res?.success) {
      setToast({ type: 'error', message: res?.message || 'Could not check these values.' });
      return;
    }

    const p: ISuppressionPreview = res.data;
    setInvalid(p.invalid);
    // Nothing readable: the list under the box says why.
    if (!p.valid) return;
    if (p.valid === p.alreadyListed) {
      setToast({ type: 'info', message: 'Everything here is already on the list.' });
      return;
    }
    if (!p.leadsAffected && !p.invalid.length) {
      await commit();
      return;
    }
    setPreview(p);
  };

  const changeType = (next: SuppressionType) => {
    setType(next);
    setInvalid([]);
  };

  const renderInvalid = (heading: string) => invalid.length > 0 && (
    <InvalidBox>
      <strong>{heading}</strong>
      <ul>
        {invalid.slice(0, 8).map((item, i) => (
          <li key={`${item.value}-${i}`}><span>{item.value}</span> — {item.reason}</li>
        ))}
        {invalid.length > 8 && <li>…and {(invalid.length - 8).toLocaleString()} more</li>}
      </ul>
    </InvalidBox>
  );

  const renderForm = () => (
    <Body>
      {/* Shown even with one list, so it is clear where the values go. */}
      {(!listsLoaded || lists.length > 0) && (
        <Field>
          <FieldLabel htmlFor="suppression-list">List</FieldLabel>
          {listsLoaded ? (
            <SelectWrap>
              <ListSelect
                id="suppression-list"
                value={listId}
                disabled={busy}
                onChange={e => { setListId(e.target.value); setInvalid([]); }}
              >
                {lists.map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
              </ListSelect>
            </SelectWrap>
          ) : (
            <SkeletonStack $gap="8px" aria-busy="true">
              <SkeletonBone $h="36px" />
              <SkeletonBone $w="45%" $h="11px" />
            </SkeletonStack>
          )}
          {selectedList && <ScopeHint>{describeListScope(selectedList, userId)}</ScopeHint>}
        </Field>
      )}

      <Segmented role="tablist">
        {ADD_TYPES.map(t => (
          <Segment
            key={t.key}
            type="button"
            role="tab"
            aria-selected={type === t.key}
            $active={type === t.key}
            disabled={busy}
            onClick={() => changeType(t.key)}
          >
            {t.label}
          </Segment>
        ))}
      </Segmented>

      <Field>
        <Text $lighter>{meta.hint}</Text>
        <ValuesBox
          autoFocus
          rows={6}
          placeholder={meta.placeholder}
          value={text}
          disabled={busy}
          onChange={e => setText(e.target.value)}
        />
        <FieldFoot>
          <span>One per line, or separated by commas.</span>
          {values.length > 0 && <strong>{plural(values.length, 'value')}</strong>}
        </FieldFoot>
      </Field>

      <Field>
        <FieldLabel htmlFor="suppression-note">Reason <span>(optional)</span></FieldLabel>
        <NoteInput
          id="suppression-note"
          placeholder="e.g. existing customer"
          value={note}
          maxLength={500}
          disabled={busy}
          onChange={e => setNote(e.target.value)}
        />
      </Field>

      {renderInvalid(`${plural(invalid.length, 'value')} could not be read:`)}
    </Body>
  );

  const renderConfirm = (p: ISuppressionPreview) => {
    const fresh = p.valid - p.alreadyListed;
    const summary = [
      plural(fresh, 'new entry', 'new entries'),
      p.alreadyListed ? `${p.alreadyListed.toLocaleString()} already listed` : '',
      p.invalid.length ? `${p.invalid.length.toLocaleString()} skipped` : '',
    ].filter(Boolean).join(' · ');

    return (
      <Body>
        {p.leadsAffected > 0 && (
          <WarnCard>
            <strong>{plural(p.leadsAffected, 'lead')}</strong> in{' '}
            <strong>{plural(p.campaignsAffected, 'running campaign')}</strong>{' '}
            match{p.leadsAffected === 1 ? 'es' : ''} and will be taken out right away.
            They stop where they are; messages already sent stay sent.
          </WarnCard>
        )}
        <Summary>{summary}</Summary>
        {renderInvalid('These could not be read and will be skipped:')}
      </Body>
    );
  };

  const fresh = preview ? preview.valid - preview.alreadyListed : 0;
  const actionLabel = preview
    ? (preview.leadsAffected ? 'Add and remove leads' : `Add ${plural(fresh, 'entry', 'entries')}`)
    : (values.length > 1 ? `Add ${values.length} to list` : 'Add to list');

  const ownList = !!selectedList?.owner && String(selectedList.owner._id) === String(userId);
  const scopeLine = !selectedList || selectedList.scope === 'all'
    ? 'No campaign in this workspace will contact them again. They are skipped at every import.'
    : selectedList.scope === 'others'
      ? `Only ${ownList ? 'your' : `${selectedList.owner?.name || 'the owner'}’s`} campaigns can contact them. Everyone else’s skip them at every import.`
      : 'Campaigns where this list is turned on will not contact them. They are skipped at every import there.';

  return (
    <CustomModal
      headingText="Add to suppression list"
      descriptionText={preview ? 'Check what happens before saving.' : scopeLine}
      closeButton
      preventClose={busy}
      bodyPadding="0 24px 0 24px"
    >
      {preview ? renderConfirm(preview) : renderForm()}

      <Footer>
        <Button
          $type="grey"
          $width="96px"
          tooltipMessage=""
          disabled={busy}
          onClickHandler={() => (preview ? setPreview(null) : hideModal())}
        >
          {preview ? 'Back' : 'Cancel'}
        </Button>
        <ActionSlot>
          <Button
            $type="blue"
            $width="auto"
            tooltipMessage=""
            $customDisabled={styles.colors.primary200}
            // Until the lists arrive the choice isn't made yet.
            disabled={!values.length || !listsLoaded}
            isLoading={busy}
            loadingText={preview ? 'Adding…' : 'Checking…'}
            onClickHandler={() => { void (preview ? commit() : check()); }}
          >
            {actionLabel}
          </Button>
        </ActionSlot>
      </Footer>
    </CustomModal>
  );
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Segmented = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  padding: 4px;
  border-radius: 8px;
  background: #f1f3f8;
`;

const Segment = styled.button<{ $active: boolean }>`
  height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? styles.colors.white : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(16, 24, 40, 0.12)' : 'none')};
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black500)};
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.14s, color 0.14s;

  &:hover:not(:disabled) { color: ${styles.colors.primary600}; }
  &:disabled { cursor: default; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  margin: 0;
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  color: ${styles.colors.black600};

  span { font-weight: 400; color: ${styles.colors.black400}; }
`;

const inputBase = `
  width: 100%;
  border: 1px solid ${styles.colors.black200};
  border-radius: 6px;
  font-family: NunitoSans;
  font-size: 14px;
  color: ${styles.colors.black600};
  box-sizing: border-box;

  &::placeholder { color: ${styles.colors.black300}; }
  &:focus { outline: none; border-color: ${styles.colors.primary600}; }
  &:disabled { background: #f8f9fb; }
`;

const ValuesBox = styled.textarea`
  ${inputBase}
  min-height: 132px;
  padding: 10px 12px;
  line-height: 22px;
  resize: vertical;
`;

const NoteInput = styled.input`
  ${inputBase}
  height: 36px;
  padding: 0 12px;
`;

const SelectWrap = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 14px;
    top: 50%;
    width: 7px;
    height: 7px;
    margin-top: -5px;
    border-right: 2px solid ${styles.colors.black400};
    border-bottom: 2px solid ${styles.colors.black400};
    transform: rotate(45deg);
    pointer-events: none;
  }
`;

const ListSelect = styled.select`
  ${inputBase}
  height: 36px;
  padding: 0 36px 0 12px;
  appearance: none;
  background: ${styles.colors.white};
  font-weight: 700;
  cursor: pointer;
`;

const ScopeHint = styled.span`
  font-size: 12px;
  color: ${styles.colors.black400};
`;

const FieldFoot = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: ${styles.colors.black400};

  strong { color: ${styles.colors.black500}; }
`;

const InvalidBox = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  background: #fbecec;
  color: #a5423e;
  font-size: 13px;

  ul { margin: 6px 0 0; padding-left: 18px; }
  li span { font-weight: 700; word-break: break-all; }
`;

const WarnCard = styled.div`
  padding: 12px 14px;
  border: 1px solid #f3dcae;
  border-radius: 8px;
  background: #fff8ea;
  color: #7a5310;
  font-size: 14px;
  line-height: 20px;
`;

const Summary = styled.div`
  font-size: 13px;
  color: ${styles.colors.black500};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

// The shared button pads its label by 4px; a label this long needs room to breathe.
// Styled from outside: styled(Button) would drop its $-props ($type, $width…).
const ActionSlot = styled.div`
  & > button { min-width: 120px; padding: 6px 16px; }
`;

export default AddToSuppressionListModal;
